/**
 * The person's own Flow settings, kept by their sandbox (vfs-service `GET/PUT /api/v1/me/settings`), so they follow
 * them to any browser and to the other Flow surfaces: whether they finished the first-run setup and what they chose
 * (their AI tool, Flow's assistant, or both), and whether Chat is on. In per-person mode the gateway routes these
 * calls to the person's own pod.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';

export const DEFAULT_SETTINGS = Object.freeze({
  onboarding: { completed: false, aiTool: false, flowAssistant: false, keySource: null, completedAt: null },
  chat: { enabled: null },
  home: { checklistDismissed: false, previewOpened: false, chatTried: false, shared: false },
  // The project tab row (utils/projectTabsModel.js, synced by utils/tabPrefsSync.js).
  tabs: { modes: { api: 'auto', mcp: 'auto', data: 'auto', traces: 'auto', quality: 'more' }, smart: true, split: false, arrived: [], historyView: 'changes', saved: false },
});

const url = () => `${config.vfsServiceUrl.replace(/\/$/, '')}/api/v1/me/settings`;

/** The settings, or `null` when vfs-service is not configured or does not know them yet (an older vfs-service). */
export async function getSettings() {
  if (!config.vfsServiceUrl) { return null; }
  const response = await getAuthorizedFetch()(url(), { headers: { Accept: 'application/json', ...buildAclContextHeaders() } });
  if (response.status === 404) { return null; }
  if (!response.ok) { throw new Error(`Settings failed (${response.status})`); }
  return response.json();
}

/** Saves part of the settings (deep-merged by vfs-service) and resolves to all of them. */
export async function updateSettings(partial) {
  if (!config.vfsServiceUrl) { return null; }
  const response = await getAuthorizedFetch()(url(), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...buildAclContextHeaders() },
    body: JSON.stringify(partial),
  });
  if (!response.ok) { throw new Error(`Saving settings failed (${response.status})`); }
  return response.json();
}

const marked = new Set();

/**
 * Records a getting-started step the Home checklist cannot tell from other data ('previewOpened', 'chatTried',
 * 'shared'), once per page load, without waiting or failing. Only in the sandbox-first experience.
 */
export function markHomeStep(step) {
  if (!config.sandboxFirst || marked.has(step)) { return; }
  marked.add(step);
  updateSettings({ home: { [step]: true } })
    .then(() => window.dispatchEvent(new CustomEvent('flow:settings-changed')))
    .catch(() => marked.delete(step));
}
